import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, filter, map, of, switchMap, tap } from 'rxjs';
import * as OrdersActions from './orders.actions';
import { OrdersService } from './orders.service';
import { OrderState } from '@us/web/shared/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MessageService } from '@us/web/shared/feature';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { EntityType, MessagingService } from '@us/web/notifications/data-access';
import { tuiIsPresent } from '@taiga-ui/cdk';
import { Store } from '@ngrx/store';
import { getErrorMessage } from '@us/web/shared/utils';

@Injectable()
export class OrdersEffects {
    private readonly actions$ = inject(Actions);
    private readonly orderService = inject(OrdersService);
    private readonly messageService = inject(MessageService);
    private readonly messagingService = inject(MessagingService);
    private readonly store = inject(Store);

    notifications$ = createEffect(
        () => {
            return this.messagingService.notifications$.pipe(
                filter(tuiIsPresent),
                filter(value => value.entity_type === EntityType.Order),
                tap(value => {
                    this.store.dispatch(OrdersActions.loadOrder({ orderId: value.entity_id }));
                })
            );
        },
        { dispatch: false }
    );

    loadOrders$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrdersActions.loadOrders),
            switchMap(() =>
                this.orderService.getOrders().pipe(
                    map(orders => OrdersActions.loadOrdersSuccess({ orders })),
                    catchError(({ error }) => {
                        this.messageService.error(error.error_description);
                        return of(OrdersActions.loadOrdersFailure({ error }));
                    })
                )
            )
        );
    });

    loadOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrdersActions.loadOrder),
            concatMap(({ orderId }) => {
                return this.orderService.getOrder(orderId).pipe(
                    map(response => OrdersActions.loadOrderSuccess({ order: response })),
                    catchError(error => {
                        return of(OrdersActions.loadOrderFailed({ error: getErrorMessage(error), orderId }));
                    })
                );
            })
        );
    });

    loadOrderPaymentStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrdersActions.loadOrderPaymentStatus),
            concatMap(({ orderId }) => {
                return this.orderService.getTransactionStatus(orderId).pipe(
                    map(response => OrdersActions.loadOrderSuccess({ order: response })),
                    catchError(({ error }) => {
                        this.messageService.error(error.error_description);
                        return of(OrdersActions.loadOrderFailed({ error, orderId }));
                    })
                );
            })
        );
    });

    loadOrderSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OrdersActions.loadOrderSuccess),
            filter(({ order }) => order.current_state === OrderState.Complete),
            map(({ order }) => OrdersActions.completeOrder({ order }))
        );
    });
}
